import { Fragment, useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { Button, SvgIcon } from "@mui/material";

import "./style.scss";
import { ReactComponent as GridIcon } from "../../assets/grid.icon.svg";
import { ReactComponent as TivaIcon } from "../../assets/tivaIcon.svg";
import { ReactComponent as PlusIcon } from "../../assets/plusIcon.svg";
import { ReactComponent as DotsIcon } from "../../assets/dots.icon.svg";
import { ReactComponent as GearIcon } from "../../assets/gearIcon.svg";
import { ReactComponent as TrashIcon } from "../../assets/trash.icon.svg";
import { Chat } from "../../common/interfaces/chats/chat.interface";
import { ThemeContainer } from "../../common/containers/theme/theme.container";
import {
    DropdownMenu,
    DropdownMenuItem,
    SettingMenu,
    SettingMenuType,
    SettingItemType
} from "../dropdown-menu/dropdown-menu.comp";

const ChatList = ({
    chats,
    settings,
    selectedChatId,
    collapsedType = false,
    showAdminSettings = false,
    handleChatDeleting
}: {
    chats: Chat[];
    settings: DropdownMenuItem[];
    selectedChatId?: string;
    collapsedType?: boolean;
    showAdminSettings?: boolean;
    handleChatDeleting: (arg1: Chat) => void;
}) => {
    const { setLeftMenuShown } = ThemeContainer.useContainer();
    const [showMoreAvailable, setShowMoreAvailable] = useState<boolean>(chats.length > 5);
    const [showMoreActive, setShowMoreActive] = useState<boolean>(true);
    let lastSectionDate: Date | null = null;

    useEffect(() => {
        setShowMoreAvailable(chats.length > 5);
    }, [chats]);

    const diffOfDays = (date1: Date, date2: Date) => {
        const millisecondsPerDay = 1000 * 60 * 60 * 24;

        return Math.floor((date1.getTime() - date2.getTime()) / millisecondsPerDay);
    };

    const formatStringDate = (date: Date) => {
        const today = new Date();

        return diffOfDays(today, date) < 1 && date.getDate() === today.getDate()
            ? "Today"
            : diffOfDays(today, date) < 2 &&
                (date.getDate() + 1 === today.getDate() ||
                    (today.getDate() === 1 && date.getMonth() + 1 === today.getMonth()))
              ? "Yesterday"
              : diffOfDays(today, date) < 7
                ? "Previous 7 days"
                : diffOfDays(today, date) < 30
                  ? "Previous 30 days"
                  : date.toLocaleString("en-us", {
                        month: "long",
                        year: date.getFullYear() !== today.getFullYear() ? "numeric" : undefined
                    });
    };

    const handleChatCreatingClick = () => {
        setLeftMenuShown(false);
    };

    const handleShowMoreClick = () => {
        setShowMoreActive(false);
    };

    return (
        <div className={`chat-list${collapsedType ? " chat-list--collapsed" : ""}`}>
            <div className="chat-list-header">
                {showAdminSettings && (
                    <>
                        <a
                            className="chat-list-header-link"
                            href={`${window.location.protocol}//admin.${window.location.host}`}
                        >
                            <SvgIcon
                                className="chat-list-header-link__icon"
                                component={GridIcon}
                                inheritViewBox
                            />
                            <span className="chat-list-header-link__text">Admin Dashboard</span>
                        </a>
                        <a className="chat-list-header-link chat-list-header-link--selected">
                            <SvgIcon
                                className="chat-list-header-link__icon chat-list-header-link__icon--fill"
                                component={TivaIcon}
                                inheritViewBox
                            />
                            <span className="chat-list-header-link__text">Assistant</span>
                        </a>
                        <div className="chat-list-separator"></div>
                    </>
                )}
                <Link
                    className="chat-list-header-link"
                    to={"/new-chat"}
                    onClick={handleChatCreatingClick}
                >
                    <SvgIcon
                        className="chat-list-header-link__icon"
                        component={PlusIcon}
                        inheritViewBox
                    />
                    <span className="chat-list-header-link__text">New Chat</span>
                </Link>
            </div>
            <div className="chat-list-separator"></div>
            <div className="chat-list-items">
                {(showMoreActive ? chats.slice(0, 5) : chats).map(chat => {
                    const currentDate = new Date(Date.parse(chat.createdAt));
                    const isSectionAvailable =
                        !lastSectionDate ||
                        formatStringDate(lastSectionDate) !== formatStringDate(currentDate);
                    if (isSectionAvailable) {
                        lastSectionDate = currentDate;
                    }
                    return (
                        <Fragment key={chat.roomId}>
                            {isSectionAvailable && (
                                <div className="chat-list-section">
                                    <span className="chat-list-section__text">
                                        {formatStringDate(currentDate)}
                                    </span>
                                </div>
                            )}
                            <div
                                className={`chat-list-item${selectedChatId === chat.roomId ? " chat-list-item--selected" : ""}`}
                            >
                                <Link
                                    className="chat-list-item__link"
                                    to={`/chat/${chat.roomId}`}
                                    onClick={() => {
                                        setLeftMenuShown(false);
                                    }}
                                >
                                    {chat.title}
                                </Link>
                                <SettingMenu
                                    className="chat-list-item__icon--more"
                                    type={SettingMenuType.more}
                                    actions={[
                                        {
                                            icon: TrashIcon,
                                            name: "Delete",
                                            type: SettingItemType.default,
                                            handler: () => {
                                                handleChatDeleting(chat);
                                            }
                                        }
                                    ]}
                                />
                            </div>
                        </Fragment>
                    );
                })}
                {showMoreAvailable && showMoreActive && (
                    <Button className="chat-list-show-more-link" onClick={handleShowMoreClick}>
                        <SvgIcon
                            className="chat-list-show-more-link__icon"
                            component={DotsIcon}
                            inheritViewBox
                        />
                        <span className="chat-list-show-more-link__title">Show more</span>
                    </Button>
                )}
            </div>
            <div className="chat-list-settings">
                <DropdownMenu
                    icon={GearIcon}
                    title={!collapsedType ? "Settings" : undefined}
                    actions={settings}
                />
            </div>
        </div>
    );
};

export default ChatList;
